// src/components/viewer/ExifDrawer.tsx
import React, { useMemo, useRef, useCallback } from 'react';
import { PhotoRecord } from '../../types/domain';
import {
  formatCamera,
  formatExposure,
  formatExifTimestamp,
  formatGpsCoordinates,
  getConfidenceMeta,
  getCategoryStyle,
} from './exifFormatter';
import {
  Camera,
  Tag,
  Calendar,
  Clock,
  MapPin,
  Compass,
  X,
  Aperture,
  Zap,
  Gauge,
  Focus,
  SlidersHorizontal,
} from 'lucide-react';

export interface ExifDrawerProps {
  photo: PhotoRecord | null | undefined;
  timezoneId?: string;
  isOpen: boolean;
  onToggle?: () => void;
  onClose?: () => void;
  onLocateOnMap?: (photo: PhotoRecord) => void;
  className?: string;
}

export const ExifDrawer: React.FC<ExifDrawerProps> = ({
  photo,
  timezoneId,
  isOpen,
  onClose,
  onLocateOnMap,
  className = '',
}) => {
  const touchStartYRef = useRef<number | null>(null);
  const drawerRef = useRef<HTMLElement | null>(null);

  // Formatted metadata
  const camera = useMemo(() => formatCamera(photo?.exif), [photo?.exif]);
  const exposure = useMemo(() => formatExposure(photo?.exif), [photo?.exif]);
  const timestamp = useMemo(
    () => formatExifTimestamp(photo?.timestampUtc || 0, timezoneId),
    [photo?.timestampUtc, timezoneId]
  );
  const coordinates = useMemo(
    () => formatGpsCoordinates(photo?.lat, photo?.lon),
    [photo?.lat, photo?.lon]
  );
  const confidence = useMemo(
    () => getConfidenceMeta(photo?.matchingConfidence || 'unmatched'),
    [photo?.matchingConfidence]
  );
  const categoryStyle = useMemo(
    () => getCategoryStyle(photo?.category),
    [photo?.category]
  );

  // Swipe-down touch gesture to collapse on mobile
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches?.[0] || (e.nativeEvent as any)?.touches?.[0];
    if (touch && typeof touch.clientY === 'number') {
      touchStartYRef.current = touch.clientY;
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartYRef.current === null) return;
      const touch = e.touches?.[0] || (e.nativeEvent as any)?.touches?.[0];
      if (!touch || typeof touch.clientY !== 'number') return;
      const currentY = touch.clientY;
      const deltaY = currentY - touchStartYRef.current;
      // If user drags down by more than 60px, close/collapse the drawer
      if (deltaY > 60 && onClose) {
        onClose();
        touchStartYRef.current = null;
      }
    },
    [onClose]
  );

  const handleTouchEnd = useCallback(() => {
    touchStartYRef.current = null;
  }, []);

  if (!photo) return null;

  return (
    <aside
      ref={drawerRef}
      role="region"
      aria-label="Metadati EXIF e Posizione"
      aria-expanded={isOpen}
      onClick={(e) => e.stopPropagation()} // Prevent PhotoSwipe lightbox gestures
      onTouchStart={(e) => {
        e.stopPropagation();
        handleTouchStart(e);
      }}
      onTouchMove={(e) => {
        e.stopPropagation();
        handleTouchMove(e);
      }}
      onTouchEnd={(e) => {
        e.stopPropagation();
        handleTouchEnd();
      }}
      className={`pointer-events-auto transition-all duration-300 ease-out z-40 ${
        isOpen
          ? 'translate-y-0 opacity-100'
          : 'translate-y-full md:translate-y-0 md:translate-x-full opacity-0 pointer-events-none'
      } fixed bottom-0 left-0 right-0 max-h-[80vh] md:max-h-[calc(100vh-140px)] md:bottom-20 md:left-auto md:right-4 md:w-96 rounded-t-3xl md:rounded-2xl bg-slate-900/80 backdrop-blur-md border border-slate-700/80 shadow-2xl overflow-hidden flex flex-col text-slate-100 font-sans pb-[env(safe-area-inset-bottom,0px)] md:pb-0 ${className}`}
    >
      {/* Mobile Swipe-Down Handle Bar */}
      <div className="pt-2.5 pb-1 flex justify-center md:hidden cursor-grab active:cursor-grabbing">
        <div className="w-12 h-1.5 bg-slate-600/80 rounded-full" />
      </div>

      {/* Header Bar */}
      <div className="px-5 py-3 border-b border-slate-800/80 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-sky-500/20 border border-sky-500/30 flex items-center justify-center text-sky-400">
            <SlidersHorizontal className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-200">
              Dettagli di Scatto
            </h2>
            <p className="text-[11px] text-slate-400 truncate max-w-[180px]">
              {photo.fileName}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 rounded-lg bg-slate-800/80 hover:bg-slate-700 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition cursor-pointer"
              aria-label="Chiudi pannello metadati"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Content Body */}
      <div className="p-5 overflow-y-auto space-y-4 text-xs scrollbar-thin scrollbar-thumb-slate-700">
        {/* 1. Category Pill & GPS Confidence Badges */}
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border flex items-center gap-1.5 shadow-sm ${categoryStyle.pillClass}`}
          >
            <Tag className="w-3 h-3" />
            <span>{photo.category || 'Generale'}</span>
          </span>

          <span
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border flex items-center gap-1.5 shadow-sm ${confidence.badgeClass}`}
            title={confidence.description}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${confidence.dotClass}`} />
            <span>{confidence.label}</span>
          </span>
        </div>

        {/* 2. Camera & Lens Specifications */}
        <div className="bg-slate-950/50 rounded-xl p-3 border border-slate-800/80 space-y-1.5">
          <div className="flex items-center gap-2 text-slate-200 font-semibold">
            <Camera className="w-4 h-4 text-sky-400 shrink-0" />
            <span className="truncate">{camera.displayName}</span>
          </div>
          {camera.lens && (
            <div className="flex items-center gap-2 text-[11px] text-slate-400 pl-6">
              <Focus className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <span className="truncate">{camera.lens}</span>
            </div>
          )}
        </div>

        {/* 3. Exposure Triangle 4-Pill Grid */}
        <div className="grid grid-cols-4 gap-2">
          {/* Aperture */}
          <div className="bg-slate-950/40 border border-slate-800/80 rounded-xl p-2.5 flex flex-col items-center justify-center text-center">
            <Aperture className="w-3.5 h-3.5 text-sky-400 mb-1" />
            <span className="text-[10px] text-slate-500 font-medium">Diaframma</span>
            <span className="text-xs font-semibold text-slate-200 font-mono mt-0.5">
              {exposure.aperture}
            </span>
          </div>

          {/* Shutter Speed */}
          <div className="bg-slate-950/40 border border-slate-800/80 rounded-xl p-2.5 flex flex-col items-center justify-center text-center">
            <Gauge className="w-3.5 h-3.5 text-amber-400 mb-1" />
            <span className="text-[10px] text-slate-500 font-medium">Tempo</span>
            <span className="text-xs font-semibold text-slate-200 font-mono mt-0.5 truncate max-w-full">
              {exposure.shutterSpeed}
            </span>
          </div>

          {/* ISO */}
          <div className="bg-slate-950/40 border border-slate-800/80 rounded-xl p-2.5 flex flex-col items-center justify-center text-center">
            <Zap className="w-3.5 h-3.5 text-emerald-400 mb-1" />
            <span className="text-[10px] text-slate-500 font-medium">Sensibilità</span>
            <span className="text-xs font-semibold text-slate-200 font-mono mt-0.5">
              {exposure.iso}
            </span>
          </div>

          {/* Focal Length */}
          <div className="bg-slate-950/40 border border-slate-800/80 rounded-xl p-2.5 flex flex-col items-center justify-center text-center">
            <Focus className="w-3.5 h-3.5 text-purple-400 mb-1" />
            <span className="text-[10px] text-slate-500 font-medium">Focale</span>
            <span className="text-xs font-semibold text-slate-200 font-mono mt-0.5">
              {exposure.focalLength}
            </span>
          </div>
        </div>

        {/* 4. Timestamp & Timezone */}
        <div className="bg-slate-950/40 border border-slate-800/80 rounded-xl p-3 space-y-2">
          <div className="flex items-center justify-between text-[11px]">
            <div className="flex items-center gap-2 text-slate-300">
              <Calendar className="w-3.5 h-3.5 text-slate-400" />
              <span>{timestamp.dateFormatted}</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-200 font-mono font-medium">
              <Clock className="w-3.5 h-3.5 text-sky-400" />
              <span>{timestamp.timeFormatted}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-slate-400 pl-5">
            <Compass className="w-3 h-3 text-slate-500" />
            <span>{timestamp.timezoneLabel}</span>
          </div>
        </div>

        {/* 5. Polarsteps Step & GPS Location */}
        <div className="bg-slate-950/40 border border-slate-800/80 rounded-xl p-3 space-y-2.5">
          {photo.stepName && (
            <div className="flex items-start gap-2">
              <MapPin className="w-3.5 h-3.5 text-rose-400 mt-0.5 shrink-0" />
              <div>
                <span className="text-[10px] text-slate-500 block">Tappa Polarsteps</span>
                <span className="font-semibold text-slate-200">{photo.stepName}</span>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-1 border-t border-slate-800/60 text-[11px]">
            <span className="text-slate-400">Coordinate:</span>
            <span className="font-mono text-slate-300 font-medium">
              {coordinates.combined}
            </span>
          </div>

          {/* Locate on Map Action Button */}
          {coordinates.isValid && onLocateOnMap && (
            <button
              type="button"
              onClick={() => onLocateOnMap(photo)}
              className="w-full mt-2 py-2 px-3 bg-slate-800 hover:bg-slate-700 active:bg-slate-800/90 text-sky-400 hover:text-sky-300 border border-sky-500/30 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 transition cursor-pointer shadow-md"
            >
              <MapPin className="w-3.5 h-3.5" />
              <span>Centra sulla Mappa</span>
            </button>
          )}
        </div>

        {/* 6. Image File Sizing & Storage Stats */}
        <div className="flex items-center justify-between text-[10px] text-slate-500 px-1">
          <span>Risoluzione HD: ~1600px WebP</span>
          <span>
            {photo.previewSize
              ? `${Math.round(photo.previewSize / 1024)} KB`
              : 'Ottimizzato'}
          </span>
        </div>
      </div>
    </aside>
  );
};

export default ExifDrawer;
